// biome-ignore format: historic
// biome-ignore format: historic
// biome-ignore format: historic
import { LockButton } from '@components/Card2/LockableTextField/LockButton';
import PhoneNumberInput, { type OnChangeDetails } from '@components/PhoneNumberInput/PhoneNumberInput';
import { useUser } from '@context/user-context';
import { useCardPopoverInputFocus } from '@hooks/card';
import useSyncWhile from '@hooks/use-sync-while';
import {
  getSuggestionsByFieldType,
  getSuggestionsFromURLFieldValue,
  getURIFromURLFieldValue,
} from '@modules/card-helpers';
import { Box, Stack } from '@ui-lib';
import { type CountryCode, isSupportedCountry, parsePhoneNumber } from 'libphonenumber-js';
import { useMemo } from 'react';
import type { CardUrlType } from 'types';

import { LabelField } from '../../LabelField';

interface PhoneNumberBasedUrlEditorProps {
  phoneCountryCode: string;
  value: string;
  shorthand: string;
  valueLocked: boolean;
  valueLockEnabled?: boolean;
  label: string;
  labelLocked: boolean;
  labelLockEnabled?: boolean;
  labelLockedByTemplate?: boolean;
  type: string;
  setPhoneCountryCode: (value: string) => void;
  setValue: (value: string) => void;
  setShorthand: (value: string) => void;
  setValueLocked: (value: boolean) => void;
  setLabel: (value: string) => void;
  setLabelLocked: (value: boolean) => void;
  setType: (value: string) => void;
}

export function PhoneNumberBasedUrlEditor({
  phoneCountryCode,
  value,
  shorthand,
  valueLocked,
  valueLockEnabled,
  label,
  labelLocked,
  labelLockEnabled,
  labelLockedByTemplate,
  type,
  setPhoneCountryCode,
  setValue,
  setShorthand,
  setValueLocked,
  setLabel,
  setLabelLocked,
}: PhoneNumberBasedUrlEditorProps): React.ReactElement {
  const { user } = useUser();

  const suggestions = useMemo(() => {
    const derivedSuggestions = getSuggestionsFromURLFieldValue(type as CardUrlType, value);
    const baseSuggestions = getSuggestionsByFieldType('url')(type as CardUrlType);
    return [...baseSuggestions, ...derivedSuggestions];
  }, [type, value]);

  useSyncWhile(
    value,
    (fieldValue) => {
      if (fieldValue) {
        // Older phone numbers may be localised, which will cause parsePhoneNumber to throw an error
        try {
          const phoneNumber = parsePhoneNumber(fieldValue);
          setPhoneCountryCode(phoneNumber.country || 'US');
          setValue(fieldValue.split(`+${phoneNumber.countryCallingCode}`)[1]);
        } catch {
          // In the case of a localised number try and fill in the country code with data we have on the user
          if (user?.country && isSupportedCountry(user.country)) {
            setPhoneCountryCode(user.country as CountryCode);
          }

          setValue(fieldValue);
        }
      }
    },
    false,
  );

  const { inputRef } = useCardPopoverInputFocus();

  const handleValueChange = (details: OnChangeDetails) => {
    const standardizedURLValue = getURIFromURLFieldValue(type as CardUrlType, details.internationalNumber);
    setValue(standardizedURLValue || '');
    setShorthand(details.number);
    setPhoneCountryCode(details.countryCode);
  };

  return (
    <Stack gap="large">
      {/* <TypeSelector type={type} onSelect={handleTypeSelect} /> */}
      <Box display="flex" gap="xsmall" alignItems="center">
        <PhoneNumberInput
          phoneNumber={shorthand}
          countryCode={phoneCountryCode as CountryCode}
          onChange={handleValueChange}
          ref={inputRef}
          disabled={valueLocked}
          required
        />
        {valueLockEnabled && (
          <LockButton locked={valueLocked} toggleLocked={() => setValueLocked(!valueLocked)} />
        )}
      </Box>
      <Stack gap="medium">
        <LabelField
          label={label}
          labelLocked={labelLocked}
          labelSuggestions={suggestions}
          setLabel={setLabel}
          setLabelLocked={labelLockEnabled ? setLabelLocked : undefined}
          labelLockedByTemplate={labelLockedByTemplate}
          subject="title"
          required
        />
      </Stack>
    </Stack>
  );
}
